/**
 * ผลรางวัล — ตรง user-classic resultDisplayUtils
 */
import type { LotteryRound } from '../services/lotteryService'
import { compareRoundsForLottoDisplay } from '../services/lotteryService'

export function getWinningNumbers(round: LotteryRound | null) {
  const wn = (round as { results?: { winningNumbers?: Record<string, string> } } | null)?.results?.winningNumbers
  if (!wn) return { first: '', threeUp: '', twoDown: '', threeDown: '', fourUp: '', twoUp: '' }
  const first = wn.first ?? (wn as Record<string, string>).FIRST_PRIZE ?? ''
  const twoDown = wn.twoDown ?? (wn as Record<string, string>).LAST_2_DIGITS ?? ''
  const threeUp = wn.threeUp ?? (wn as Record<string, string>)['3_DIGIT_TOP'] ?? ''
  const threeDown =
    wn.threeDown ??
    (wn as Record<string, string>)['3_DIGIT_BOTTOM_FRONT_1'] ??
    (wn as Record<string, string>)['3_DIGIT_BOTTOM_BACK_1'] ??
    ''
  return {
    first,
    threeUp: threeUp || (first.length >= 3 ? first.slice(0, 3) : ''),
    twoDown,
    threeDown,
    fourUp: (wn as Record<string, string>).fourUp ?? (wn as Record<string, string>)['4_DIGIT_UP'] ?? '',
    twoUp:
      (wn as Record<string, string>).twoUp ??
      (wn as Record<string, string>)['2_DIGIT_UP'] ??
      (first.length >= 2 ? first.slice(0, 2) : ''),
  }
}

export function hasAnyResult(round: LotteryRound | null): boolean {
  const w = getWinningNumbers(round)
  return !!(w.first || w.threeUp || w.twoDown || w.threeDown || w.fourUp || w.twoUp)
}

export function isRoundCancelled(round: LotteryRound | null | undefined): boolean {
  return String(round?.status || '').toLowerCase() === 'cancelled'
}

export function displayValue(
  value: string,
  options: { isCancelled?: boolean; hasResult?: boolean } = {}
): string {
  const { isCancelled = false, hasResult = true } = options
  if (isCancelled) return 'ยกเลิก'
  if (!hasResult || !value) return 'รอผล'
  return value
}

/** เรียงรอบบนหน้าผล — งวดตรงวันที่เลือกก่อน แล้วใช้ลำดับเดียวกับหน้า lobby (เปิด → ยังไม่เปิด → ปิด/ยกเลิก) */
export function sortRoundsForResultDisplay<T extends LotteryRound>(list: T[], selectedDate: string): T[] {
  return [...list].sort((a, b) => {
    const aDraw = getRoundDrawDateString(a)
    const bDraw = getRoundDrawDateString(b)
    const aMatch = aDraw === selectedDate ? 1 : 0
    const bMatch = bDraw === selectedDate ? 1 : 0
    if (bMatch !== aMatch) return bMatch - aMatch
    return compareRoundsForLottoDisplay(a, b)
  })
}

/** งวด: DD MMM YYYY (en-US for month short) */
function formatDrawLabelFromDate(date: Date): string {
  const f = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Bangkok',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
  const parts = f.formatToParts(date)
  const day = parts.find((p) => p.type === 'day')?.value ?? String(date.getDate())
  const month = parts.find((p) => p.type === 'month')?.value ?? ''
  const year = parts.find((p) => p.type === 'year')?.value ?? String(date.getFullYear())
  return `งวด: ${day} ${month} ${year}`
}

/** งวดจากรอบ — fallback เป็นวันที่ที่เลือกบนหน้าเมื่อ API ไม่มี betEndTime */
export function getRoundDrawLabel(round: LotteryRound | null, fallbackDateStr?: string): string {
  if (round?.betEndTime) {
    const d = new Date(round.betEndTime)
    if (!Number.isNaN(d.getTime())) return formatDrawLabelFromDate(d)
  }
  if (round?.drawDate) {
    const d = new Date(round.drawDate)
    if (!Number.isNaN(d.getTime())) return formatDrawLabelFromDate(d)
  }
  const fromRoundNumber = round?.roundNumber ? getDateStringFromRoundNumber(round.roundNumber) : null
  if (fromRoundNumber) {
    const d = new Date(`${fromRoundNumber}T12:00:00+07:00`)
    if (!Number.isNaN(d.getTime())) return formatDrawLabelFromDate(d)
  }
  if (fallbackDateStr?.trim()) {
    const d = new Date(`${fallbackDateStr.trim()}T12:00:00+07:00`)
    if (!Number.isNaN(d.getTime())) return formatDrawLabelFromDate(d)
  }
  return 'งวด: -'
}

export function getDrawLabelNgod(round: LotteryRound, fallbackDateStr?: string): string {
  return getRoundDrawLabel(round, fallbackDateStr)
}

const BANGKOK = 'Asia/Bangkok'

function toDateStringBangkok(iso: string): string {
  const f = new Intl.DateTimeFormat('en-CA', { timeZone: BANGKOK, year: 'numeric', month: '2-digit', day: '2-digit' })
  const parts = f.formatToParts(new Date(iso))
  const y = parts.find((p) => p.type === 'year')!.value
  const m = parts.find((p) => p.type === 'month')!.value
  const d = parts.find((p) => p.type === 'day')!.value
  return `${y}-${m}-${d}`
}

export function isRoundVisibleOnDate(round: LotteryRound, dateStr: string): boolean {
  if (!dateStr?.trim()) return true
  const startDate = toDateStringBangkok(round.betStartTime)
  const endDate = toDateStringBangkok(round.betEndTime)
  const drawDate = round.drawDate ? toDateStringBangkok(round.drawDate) : endDate
  if (drawDate === dateStr) return true
  if (dateStr >= startDate && dateStr <= endDate) return true
  return false
}

export function getRoundDrawDateString(round: LotteryRound): string {
  return round.drawDate ? toDateStringBangkok(round.drawDate) : toDateStringBangkok(round.betEndTime)
}

export function getDateStringFromRoundNumber(roundNumber: string | undefined): string | null {
  if (!roundNumber?.trim()) return null
  const m = roundNumber.match(/^[^-]+-(\d{8})-/)
  if (!m) return null
  const y = m[1].slice(0, 4)
  const mo = m[1].slice(4, 6)
  const d = m[1].slice(6, 8)
  return `${y}-${mo}-${d}`
}

export function isRoundNumberDateExactly(round: LotteryRound, dateStr: string): boolean {
  if (!dateStr?.trim()) return true
  const fromRoundNumber = getDateStringFromRoundNumber(round.roundNumber)
  if (fromRoundNumber != null) return fromRoundNumber === dateStr
  return getRoundDrawDateString(round) === dateStr
}
